import { buildSearchParamsAuto } from '../utils';
import {
  STRATEGY_TAG_TYPE,
  ALL_STRATEGIES_ID,
  USER_STRATEGIES_ID,
  MAP_MEMBERS_ID,
  LIST_MEMBERS_ID,
  TERRITORIES_ID,
} from './constants';
import { strategyApi } from './strategyApi';
import type {
  TargetStrategyDetail,
  StrategyMapMember,
  StrategyProviderConnectorWithMetrics,
  FilterOption,
  StrategyHistoryEntry,
  StrategyFilterParams,
  StrategyMapParams,
} from './types';

interface MyStrategiesParams {
  q?: string;
  [key: string]: any;
}

export const targetStrategyApi = strategyApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllTargetStrategies: builder.query<Record<string, any>, MyStrategiesParams | void>({
      query: (params) => {
        const queryParams = buildSearchParamsAuto(params || {});
        const queryString = queryParams?.toString();
        return `v1/strategies/target-strategies/?${queryString}`;
      },
      providesTags: (result) => {
        const strategies = Array.isArray(result) ? result : [];
        return [
          {
            type: STRATEGY_TAG_TYPE,
            id: ALL_STRATEGIES_ID,
          },
          ...strategies.map(({ id }: any) => ({
            type: STRATEGY_TAG_TYPE,
            id,
          })),
        ];
      },
    }),
    getMyTargetStrategies: builder.query<Record<string, any>, MyStrategiesParams | void>({
      query: (params) => {
        const queryParams = buildSearchParamsAuto(params || {});
        return {
          url: `v1/strategies/target-strategies/assigned-strategies/?${queryParams?.toString()}`,
        };
      },
      providesTags: (result) => {
        const strategies = Array.isArray(result) ? result : [];
        return [
          {
            type: STRATEGY_TAG_TYPE,
            id: USER_STRATEGIES_ID,
          },
          ...strategies.map(({ id }: any) => ({
            type: STRATEGY_TAG_TYPE,
            id,
          })),
        ];
      },
    }),
    getTargetStrategyDetails: builder.query<TargetStrategyDetail, string>({
      query: (id) => `v1/strategies/target-strategies/${id}/`,
      providesTags: (result, error, id) => [{ type: STRATEGY_TAG_TYPE, id }],
    }),
    getTerritoriesForStrategy: builder.query<MedScout.PaginatedResponse & { results: (MedScout.Territory & { status?: string; list_members_count?: number; is_sub_territory?: boolean })[] }, { strategyId: string; page?: number; pageSize?: number; q?: string }>({
      query: ({ strategyId, page, pageSize, q }) => ({
        url: `v1/strategies/target-strategies/assigned-strategies/${strategyId}/territories/`,
        params: {
          page,
          pageSize,
          q,
        },
      }),
      providesTags: (result) => {
        const territories = result?.results || [];
        return [
          { type: STRATEGY_TAG_TYPE, id: TERRITORIES_ID }, // Add this
          ...territories.map(({ id }: any) => ({
            type: STRATEGY_TAG_TYPE,
            id,
          })),
        ];
      },
    }),
    getListMembers: builder.infiniteQuery<
      {
        results: any[];
        count?: number;
        next?: string | null;
        previous?: string | null;
      },
      {
        strategyId: string;
        territoryId: string;
        bounds?: any;
        pageSize?: number;
        [key: string]: any;
      },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
        getPreviousPageParam: (_firstPage, _allPages, firstPageParam) =>
          firstPageParam > 1 ? firstPageParam - 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const { strategyId, territoryId, bounds, pageSize, ...restParams } =
          queryArg;

        // Only include bounds if they're valid (not all zeros)
        const hasValidBounds =
          bounds &&
          (bounds.min_lat !== 0 ||
            bounds.max_lat !== 0 ||
            bounds.min_lng !== 0 ||
            bounds.max_lng !== 0);

        const searchParams = buildSearchParamsAuto(
          {
            ...restParams,
            page: pageParam,
            pageSize: pageSize || 50,
            ...(hasValidBounds && {
              min_lat: bounds.min_lat,
              max_lat: bounds.max_lat,
              min_lng: bounds.min_lng,
              max_lng: bounds.max_lng,
            }),
          },
          {
            cities: 'city',
            states: 'state',
            specialties: 'specialty',
            statuses: 'crm_status',
            provider_lists: 'provider_list',
          }
        );

        return {
          url: `v1/strategies/target-strategies/assigned-strategies/${strategyId}/territory/${territoryId}/members/?${searchParams.toString()}`,
        };
      },
      providesTags: [{ type: STRATEGY_TAG_TYPE, id: LIST_MEMBERS_ID }],
    }),
    getStrategyMapMembers: builder.query<StrategyMapMember[], StrategyMapParams>({
      query: (params) => {
        const { strategyId, territoryId, bounds, ...queryParams } = params;

        // Only include bounds if they're valid (not all zeros)
        const hasValidBounds =
          bounds &&
          (bounds.min_lat !== 0 ||
            bounds.max_lat !== 0 ||
            bounds.min_lng !== 0 ||
            bounds.max_lng !== 0);

        const searchParams = buildSearchParamsAuto(
          {
            ...queryParams,
            ...(hasValidBounds && {
              min_lat: bounds.min_lat,
              max_lat: bounds.max_lat,
              min_lng: bounds.min_lng,
              max_lng: bounds.max_lng,
            }),
          },
          {
            cities: 'city',
            states: 'state',
            specialties: 'specialty',
            statuses: 'crm_status',
            provider_lists: 'provider_list',
          }
        );

        return {
          url: `v1/strategies/target-strategies/assigned-strategies/${strategyId}/territory/${territoryId}/map/?${searchParams.toString()}`,
        };
      },
      providesTags: [{ type: STRATEGY_TAG_TYPE, id: MAP_MEMBERS_ID }],
    }),
    getTargetStrategyProviderDetails: builder.query<StrategyProviderConnectorWithMetrics, { strategyId: string; providerId: string }>({
      query: ({ strategyId, providerId }) => ({
        url: `v1/strategies/target-strategies/${strategyId}/provider/${providerId}/`,
      }),
      providesTags: (result, error, { strategyId, providerId }) => [
        { type: STRATEGY_TAG_TYPE, id: `${strategyId}-${providerId}` },
      ],
    }),
    createTargetStrategy: builder.mutation<TargetStrategyDetail, Record<string, any>>({
      query: (strategy) => ({
        url: 'v1/strategies/target-strategies/',
        method: 'POST',
        body: strategy,
      }),
      invalidatesTags: [
        {
          type: STRATEGY_TAG_TYPE,
          id: ALL_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: USER_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: TERRITORIES_ID,
        },
      ],
    }),
    updateTargetStrategy: builder.mutation<TargetStrategyDetail, Record<string, any> & { id: string | number }>({
      query: (strategy) => ({
        url: `v1/strategies/target-strategies/${strategy.id}/`,
        method: 'PATCH',
        body: strategy,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: STRATEGY_TAG_TYPE, id: id.toString() },
        { type: STRATEGY_TAG_TYPE, id: Number(id) },
        {
          type: STRATEGY_TAG_TYPE,
          id: ALL_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: USER_STRATEGIES_ID,
        },
      ],
    }),
    deleteTargetStrategy: builder.mutation<void, string>({
      query: (id) => ({
        url: `v1/strategies/target-strategies/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: STRATEGY_TAG_TYPE, id },
        {
          type: STRATEGY_TAG_TYPE,
          id: ALL_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: USER_STRATEGIES_ID,
        },
      ],
    }),
    // Filter Options
    getSpecialtyFilterOptions: builder.query<FilterOption[], StrategyFilterParams>({
      query: ({ strategyId, territoryId, ...rest }) => {
        const queryParams = buildSearchParamsAuto(rest);
        const queryString = queryParams?.toString();
        return {
          url: `v1/strategies/target-strategies/assigned-strategies/${strategyId}/territory/${territoryId}/filter-options/specialty/?${queryString}`,
        };
      },
    }),
    getStatusFilterOptions: builder.query<FilterOption[], StrategyFilterParams>({
      query: ({ strategyId, territoryId, ...rest }) => {
        const queryParams = buildSearchParamsAuto(rest);
        const queryString = queryParams?.toString();
        return {
          url: `v1/strategies/target-strategies/assigned-strategies/${strategyId}/territory/${territoryId}/filter-options/crm-status/?${queryString}`,
        };
      },
    }),
    getListMembershipFilterOptions: builder.query<FilterOption[], StrategyFilterParams>({
      query: ({ strategyId, territoryId, ...rest }) => {
        const queryParams = buildSearchParamsAuto(rest);
        const queryString = queryParams?.toString();
        return {
          url: `v1/strategies/target-strategies/assigned-strategies/${strategyId}/territory/${territoryId}/filter-options/list-membership/?${queryString}`,
        };
      },
    }),
    getStateFilterOptions: builder.query<FilterOption[], StrategyFilterParams>({
      query: ({ strategyId, territoryId, q }) => ({
        url: `v1/strategies/target-strategies/assigned-strategies/${strategyId}/territory/${territoryId}/filter-options/state/`,
        params: {
          ...(q && q !== '' && { q }),
        },
      }),
    }),
    getCityFilterOptions: builder.query<FilterOption[], StrategyFilterParams>({
      query: ({ strategyId, territoryId, ...rest }) => {
        const queryParams = buildSearchParamsAuto(rest);
        const queryString = queryParams?.toString();
        return {
          url: `v1/strategies/target-strategies/assigned-strategies/${strategyId}/territory/${territoryId}/filter-options/city/?${queryString}`,
        };
      },
    }),
    // Connectors
    getTargetStrategyConnector: builder.query<
      {
        id: string | number;
        provider_id: string;
        provider__name: string;
        status: string;
        list_items_count: number;
        last_refreshed: string | null;
        metrics: Record<string, Record<string, number>>;
      },
      { strategyId: string; territoryId: string }
    >({
      query: ({ strategyId, territoryId }) =>
        `v1/strategies/target-strategies/${strategyId}/territories/${territoryId}/connector/`,
      providesTags: (result, error, { strategyId, territoryId }) => [
        {
          type: STRATEGY_TAG_TYPE,
          id: `CONNECTOR_${strategyId}_${territoryId}`,
        },
      ],
    }),
    getTargetStrategyConnectors: builder.infiniteQuery<
      {
        results: Array<{
          id: string | number;
          status: string;
          territory__name: string;
          list_items_count: number;
        }>;
        count?: number;
        next?: string | null;
        previous?: string | null;
      },
      { strategyId: string | null; params?: any },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
        getPreviousPageParam: (firstPage, allPages, firstPageParam) =>
          firstPageParam > 1 ? firstPageParam - 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const { page_size, ...restParams } = queryArg.params || {};
        const searchParams = buildSearchParamsAuto({
          ...restParams,
          page: pageParam,
          page_size: page_size || 50,
        });
        return `v1/strategies/target-strategies/${
          queryArg.strategyId
        }/connectors/?${searchParams.toString()}`;
      },
    }),
    getTargetStrategyHeatmap: builder.query<
      {
        points: Array<{
          lat: number;
          lng: number;
          volume: number;
        }>;
      },
      { strategyId: string; territoryId: string; params?: { metric?: string } }
    >({
      query: ({ strategyId, territoryId, params }) => {
        const searchParams = buildSearchParamsAuto(params || {});
        const queryString = searchParams?.toString();
        return `v1/strategies/target-strategies/assigned-strategies/${strategyId}/territory/${territoryId}/heat-map/?${queryString}`;
      },
    }),
    // History
    getTargetStrategyHistory: builder.query<MedScout.PaginatedResponse & { results: StrategyHistoryEntry[] }, { strategyId: string }>({
      query: ({ strategyId }) =>
        `v1/strategies/target-strategies/${strategyId}/history/`,
    }),
  }),
});

export const {
  useGetAllTargetStrategiesQuery,
  useGetMyTargetStrategiesQuery,
  useGetTargetStrategyDetailsQuery,
  useGetTerritoriesForStrategyQuery,
  useGetListMembersInfiniteQuery,
  useGetStrategyMapMembersQuery,
  useGetTargetStrategyProviderDetailsQuery,
  useCreateTargetStrategyMutation,
  useUpdateTargetStrategyMutation,
  useDeleteTargetStrategyMutation,
  useGetSpecialtyFilterOptionsQuery,
  useGetStatusFilterOptionsQuery,
  useGetListMembershipFilterOptionsQuery,
  useGetStateFilterOptionsQuery,
  useGetCityFilterOptionsQuery,
  useGetTargetStrategyConnectorsInfiniteQuery,
  useGetTargetStrategyConnectorQuery,
  useGetTargetStrategyHeatmapQuery,
  useGetTargetStrategyHistoryQuery,
} = targetStrategyApi;
